import { useEffect, useRef, useState } from "react";
import { getRolePreview } from "../../shared/api/client";
import { readProductionLineMonitor, LineMonitorReadError } from "./line-monitor-gateway";
import { acceptLineMonitorResponse, initialLineMonitorState, presentLineMonitor } from "./production-line-monitor-presentation";
import type { LineMonitorState } from "./production-line-monitor-presentation";

export function useProductionLineMonitorWorkspace() {
  const [state, setState] = useState<LineMonitorState>(initialLineMonitorState);
  const [now, setNow] = useState(Date.now);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [motionPaused, setMotionPaused] = useState(false);
  const [visible, setVisible] = useState(() => !document.hidden);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (getRolePreview()) {
      setState({ ...initialLineMonitorState, phase: "unavailable", error: "Rol önizlemesinde gerçek hat verileri açılmaz. Kurumsal oturumunuzla giriş yapın." });
      return;
    }
    let stopped = false;
    let timer: number | undefined;
    let request: AbortController | null = null;
    const read = async () => {
      if (stopped || document.hidden) return;
      request = new AbortController();
      const timeout = window.setTimeout(() => request?.abort(), 8000);
      let delay = 2000;
      let stopPolling = false;
      try {
        const response = await readProductionLineMonitor(request.signal);
        if (!stopped && !document.hidden) setState(current => acceptLineMonitorResponse(current, response, Date.now()));
        if (!response.available) delay = 15_000;
      } catch (error) {
        if (stopped || document.hidden) return;
        const status = error instanceof LineMonitorReadError ? error.status : null;
        const denied = status === 401 || status === 403;
        stopPolling = denied;
        delay = 10_000;
        setState(current => denied ? {
          ...initialLineMonitorState, phase: status === 401 ? "unauthorized" : "forbidden", error: null,
        } : {
          ...current, phase: "error",
          error: status === 404 ? "Bu sunucuda canlı hat izleme servisi henüz etkin değil."
            : request?.signal.aborted ? "Sunucu yanıtı gecikti. Son doğrulanmış değerler korunuyor."
              : "Hat verisi alınamadı. Son doğrulanmış değerler korunuyor.",
        });
      } finally {
        window.clearTimeout(timeout);
        request = null;
        if (!stopped && !document.hidden && !stopPolling) timer = window.setTimeout(() => void read(), delay);
      }
    };
    const onVisibility = () => {
      const shown = !document.hidden;
      setVisible(shown);
      if (shown) setNow(Date.now());
      window.clearTimeout(timer);
      if (!shown) request?.abort();
      else if (!request) void read();
    };
    document.addEventListener("visibilitychange", onVisibility);
    void read();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      request?.abort();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [reloadVersion]);

  useEffect(() => {
    const timer = window.setInterval(() => { if (!document.hidden) setNow(Date.now()); }, 1000);
    const fullscreen = () => setExpanded(document.fullscreenElement === panel.current);
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape" && !document.fullscreenElement) setExpanded(false); };
    document.addEventListener("fullscreenchange", fullscreen);
    window.addEventListener("keydown", escape);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("fullscreenchange", fullscreen);
      window.removeEventListener("keydown", escape);
    };
  }, []);

  const toggleExpanded = async () => {
    if (document.fullscreenElement === panel.current && document.fullscreenElement) {
      await document.exitFullscreen().catch(() => setExpanded(false));
    } else if (document.fullscreenEnabled && panel.current) {
      await panel.current.requestFullscreen().catch(() => setExpanded(value => !value));
    } else setExpanded(value => !value);
  };
  return {
    state, ...presentLineMonitor(state, now), panel, expanded,
    motionPaused, toggleMotion: () => setMotionPaused(value => !value), visible,
    toggleExpanded, refresh: () => setReloadVersion(value => value + 1),
  };
}
